<script>
export default {
  name: 'HrmineChat',
  props: {
    apiUrl: {
      type: String,
      default: '/api/ai-chat/chat/'
    },
    /**
     * Which shell this chat runs in: 'web' on a CMS page, 'app' in zpcard.
     *
     * The answer carries links, and the two shells have different address
     * spaces -- the app routes anything starting with "/" through its own
     * router, where a CMS path leads nowhere.
     */
    client: {
      type: String,
      default: 'web'
    },
    initialMessage: {
      type: String,
      default: ''
    },
    useFetch: {
      type: Boolean,
      default: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Stell eine Frage...'
    },
    suggestions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      messages: [],
      input: '',
      loading: false,
      activePlaceholder: this.placeholder,
      chips: []
    }
  },
  computed: {
    showSuggestions() {
      return this.suggestions.length && !this.messages.length
    }
  },
  mounted() {
    // Restore previous session from localStorage (skipped when initialMessage is set)
    if (!this.initialMessage) {
      const saved = localStorage.getItem('hrmine_chat_history')
      if (saved) {
        try { this.messages = JSON.parse(saved) } catch (e) { /* ignore invalid cache */ }
      }
    }
    if (this.initialMessage) {
      this.send(this.initialMessage)
    }
  },
  watch: {
    placeholder(val) {
      if (!this.messages.length) this.activePlaceholder = val
    },
    messages: {
      deep: true,
      handler(val) {
        // Keep last 50 messages in localStorage to prevent unbounded growth
        var toSave = val.length > 50 ? val.slice(-50) : val
        localStorage.setItem('hrmine_chat_history', JSON.stringify(toSave))
      }
    },
    // Scroll watcher (kept separate to avoid merging with deep watcher above)
    'messages.length'() {
      this.$nextTick(() => {
        if (this.$refs.messages) {
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
        }
      })
    }
  },
  methods: {
    renderMarkdown(text) {
      if (!text) return ''
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // Code blocks
      html = html.replace(/```(\w*)\n([\s\S]*?)```/g, function(_, lang, code) {
        return '<pre><code>' + code.replace(/\n$/, '') + '</code></pre>'
      })
      // Inline code
      html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
      // Headings
      html = html.replace(/^#### (.+)$/gm, '<h6>$1</h6>')
      html = html.replace(/^### (.+)$/gm, '<h5>$1</h5>')
      html = html.replace(/^## (.+)$/gm, '<h4>$1</h4>')
      html = html.replace(/^# (.+)$/gm, '<h3>$1</h3>')
      // Bold + Italic
      html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
      // Bold
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic
      html = html.replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Links: internal (starting with /) use SPA navigation, external open in new tab
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, function(_, text, href) {
        if (href.startsWith('/')) {
          return '<a href="' + href + '" data-spa-link="true">' + text + '</a>'
        }
        return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + text + '</a>'
      })
      // Unordered list items
      html = html.replace(/^[\s]*[-*]\s+(.+)$/gm, '<li>$1</li>')
      // Ordered list items
      html = html.replace(/^[\s]*\d+\.\s+(.+)$/gm, '<li>$1</li>')
      // Normalize double newlines between list items before paragraph conversion
      html = html.replace(/<\/li>\n\n<li>/g, '</li>\n<li>')
      // Wrap consecutive <li> in <ul>
      html = html.replace(/((?:<li>[\s\S]*?<\/li>\s*)+)/g, '<ul>$1</ul>')
      // Paragraphs
      html = html.replace(/\n\n/g, '</p><p>')
      // Newlines after block elements
      html = html.replace(/(<\/(?:pre|h[3-6]|ul)>)\n/g, '$1')
      html = html.replace(/\n/g, '<br>')
      html = html.replace(/<br><ul>/g, '<ul>')
      html = html.replace(/<\/ul><br>/g, '</ul>')
      // Remove <br> between list items (from \n -> <br> conversion)
      html = html.replace(/<\/li><br><li>/g, '</li><li>')
      return '<p>' + html + '</p>'
    },
    getCsrfToken() {
      for (let c of document.cookie.split(';')) {
        const [n, v] = c.trim().split('=')
        if (n === 'csrftoken') return v
      }
      const meta = document.querySelector('meta[name="csrf-token"]')
      if (meta) return meta.getAttribute('content')
      return ''
    },
    async send(text) {
      const msg = typeof text === 'string' ? text.trim() : this.input.trim()
      if (!msg || this.loading) return
      if (msg.length > 500) return

      this.input = ''
      this.chips = []
      this.messages.push({ role: 'user', content: msg })
      this.loading = true

      // Build conversation history: last 10 message pairs (20 messages) for LLM context
      const all = this.messages.slice(0, -1)
      const history = all.slice(-20).map(m => ({
        role: m.role, content: m.content
      }))

      if (!this.useFetch) {
        this.$emit('send', msg, history)
        return
      }

      try {
        const res = await fetch(this.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken()
          },
          body: JSON.stringify({ message: msg, history, client: this.client })
        })
        if (res.status === 429) {
          this.messages.push({
            role: 'assistant',
            content: 'Zu viele Anfragen. Bitte warte einen Moment.'
          })
          return
        }
        if (!res.ok) throw new Error('HTTP ' + res.status)
        const data = await res.json()
        this.messages.push({
          role: 'assistant',
          content: data.response || data.error || 'Keine Antwort erhalten.',
          sources: data.sources || []
        })
        this.chips = data.chips || []
      } catch (e) {
        console.error('Chat error:', e)
        this.messages.push({
          role: 'assistant',
          content: 'Ein Fehler ist aufgetreten. Bitte versuch es erneut.'
        })
      } finally {
        this.loading = false
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus()
        })
      }
    },
    clearChat() {
      this.messages = []
      this.chips = []
      this.input = ''
      localStorage.removeItem('hrmine_chat_history')
    },
    adoptChip(text) {
      this.input = this.input ? this.input + ', ' + text : text
      this.$nextTick(() => this.$refs.input.focus())
    },
    adoptPlaceholder() {
      if (!this.messages.length && !this.input) {
        this.input = this.activePlaceholder
        this.$nextTick(() => this.$refs.input.select())
      }
    },
    handleResponseClick(event) {
      const link = event.target.closest('a[data-spa-link]')
      if (!link) return
      event.preventDefault()
      const href = link.getAttribute('href')
      if (this.$router) {
        this.$router.push(href).catch(() => {})
      } else {
        window.location.href = href
      }
    },
    // Public method for parents using useFetch=false to push assistant responses
    addResponse(content, sources) {
      this.messages.push({
        role: 'assistant',
        content: content || 'Keine Antwort erhalten.',
        sources: sources || []
      })
      this.loading = false
    }
  }
}
</script>

<template>
  <div class="d-flex flex-column h-100">
    <!-- Messages -->
    <div class="flex-grow-1 overflow-auto p-3" ref="messages">
      <!-- Empty state -->
      <div v-if="!messages.length" class="text-center text-muted py-5">
        <i class="bi bi-chat-dots display-4 d-block mb-2"></i>
        <p>{{ subtitle || activePlaceholder }}</p>
        <div v-if="showSuggestions" class="d-flex flex-wrap justify-content-center gap-1 mt-2">
          <button v-for="s in suggestions" :key="s" @click="send(s)"
                  class="btn btn-outline-secondary btn-sm rounded-pill"
                  style="font-size:.75rem">{{ s }}</button>
        </div>
      </div>
      <!-- Message bubbles -->
      <div v-for="(msg, i) in messages" :key="i" :class="['mb-3', msg.role === 'user' ? 'text-end' : '']">
        <div :class="['d-inline-block p-2 px-3 rounded-3', msg.role === 'user' ? 'bg-primary text-white' : 'bg-light border']"
             style="max-width: 85%; text-align: left;">
          <div v-if="msg.role === 'assistant'" class="ai-response"
               v-html="renderMarkdown(msg.content)"
               @click="handleResponseClick($event)"></div>
          <span v-else>{{ msg.content }}</span>
        </div>
        <!-- Sources: internal URLs (/) routed via SPA, external open in new tab -->
        <div v-if="msg.sources && msg.sources.length" class="mt-1 small"
             @click="handleResponseClick($event)">
          <span class="text-muted fw-semibold d-block">Quellen:</span>
          <a v-for="(s, j) in msg.sources" :key="j" :href="s.url"
             v-bind="s.url && s.url.startsWith('/') ? {'data-spa-link': 'true'} : {target: '_blank', rel: 'noopener noreferrer'}"
             class="text-primary me-2 small">{{ s.title }}</a>
        </div>
      </div>
      <!-- Loading -->
      <div v-if="loading" class="text-muted small p-2">
        <span class="spinner-border spinner-border-sm me-1"></span> Denke nach...
      </div>
    </div>
    <!-- Chips -->
    <div v-if="chips.length" class="d-flex flex-wrap gap-1 px-2 pb-2">
      <button
        v-for="chip in chips"
        :key="chip"
        @click="adoptChip(chip)"
        class="btn btn-outline-primary btn-sm rounded-pill"
        style="font-size:.75rem">
        {{ chip }}
      </button>
    </div>
    <!-- Input -->
    <div class="border-top bg-light p-2">
      <div class="input-group">
        <input v-model="input" @keyup.enter="send()" @focus="adoptPlaceholder"
               class="form-control" maxlength="500"
               :placeholder="messages.length ? 'Stell eine Frage...' : activePlaceholder"
               :disabled="loading" ref="input">
        <button @click="send()" class="btn btn-primary" :disabled="loading || !input.trim()">
          <i class="bi bi-send"></i>
        </button>
      </div>
      <div class="d-flex justify-content-between text-muted small">
        <a v-if="messages.length" href="#" @click.prevent="clearChat" class="text-muted text-decoration-none">
          <i class="bi bi-plus-circle me-1"></i>Neuer Chat
        </a>
        <span v-else></span>
        <span>{{ input.length }}/500</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ai-response /deep/ li {
  margin-bottom: 0.3rem;
}
.ai-response /deep/ ul,
.ai-response /deep/ ol {
  margin-bottom: 0.5rem;
}
</style>
